import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import AppShell from '@/components/AppShell';
import ConfirmSheet from '@/components/ConfirmSheet';
import { ArrowLeft, ShoppingBag, Edit3, Trash2, RotateCcw, PackageX, Eye, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "My Requirements" — buyers manage their own requirement posts here.
 * Actions: view, edit, close (mark as fulfilled), reopen, delete.
 */
export default function MyRequirementsPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // { action, post }
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/requirements/mine');
      setPosts(data.posts || []);
    } catch {
      toast.error('Failed to load your requirements');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diff < 60) return `${Math.max(diff, 1)}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const confirmAction = async () => {
    if (!pending) return;
    const { action, post } = pending;
    setBusy(true);
    try {
      if (action === 'close') {
        await api.put(`/requirements/${post.id}`, { is_open: false });
        toast.success('Requirement closed');
      } else if (action === 'reopen') {
        await api.put(`/requirements/${post.id}`, { is_open: true });
        toast.success('Requirement reopened');
      } else if (action === 'delete') {
        await api.delete(`/requirements/${post.id}`);
        toast.success('Requirement deleted');
      }
      setPending(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell showNav={false}>
      <div className="p-4 pb-16" data-testid="my-requirements-page">
        <div className="flex items-center gap-3 mb-5">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/profile')}
            className="w-9 h-9 rounded-full bg-[#EFF2F7] flex items-center justify-center"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4 text-[#0B1620]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#0B1620] font-['Bricolage_Grotesque']">My requirements</h1>
            <p className="text-xs text-[#5B6675] mt-0.5">Manage the produce you&apos;re looking to buy.</p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-28 rounded-2xl bg-[#EFF2F7] animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-[#F6F8FB] border border-[#E4E8EF] flex items-center justify-center mb-4">
              <ShoppingBag className="w-7 h-7 text-[#0B4F3F]/60" />
            </div>
            <p className="text-[#0B1620] text-sm font-semibold">No requirements yet</p>
            <p className="text-xs text-[#5B6675] mt-1">Post what you need and farmers will respond.</p>
            <button
              data-testid="post-first-requirement-btn"
              onClick={() => {
                localStorage.setItem('frootex_last_post_type', 'requirement');
                navigate('/create-post');
              }}
              className="mt-4 inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-[#0B4F3F] text-[#F6F8FB] text-sm font-semibold hover:bg-[#083D31] transition-colors"
            >
              Post a requirement
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.id}
                data-testid={`my-requirement-${post.id}`}
                className="bg-[#F6F8FB] rounded-2xl border border-[#E4E8EF] p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/requirement/${post.id}`)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                        post.is_open
                          ? 'bg-[#15803D]/10 text-[#15803D]'
                          : 'bg-[#5B6675]/10 text-[#5B6675]'
                      }`}>
                        {post.is_open ? 'Open' : 'Closed'}
                      </span>
                      <span className="text-[10px] text-[#5B6675] flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {timeAgo(post.created_at)}
                      </span>
                    </div>
                    <p className="text-sm font-semibold text-[#0B1620] line-clamp-1">{post.title}</p>
                    <p className="text-[11px] text-[#5B6675] mt-0.5">
                      ₹{post.price_per_unit}/{post.unit} · {post.quantity} {post.unit}
                      {post.location ? (
                        <> · <MapPin className="w-3 h-3 inline mx-0.5" />{post.location}</>
                      ) : null}
                    </p>
                  </button>
                  <span className="text-[10px] text-[#5B6675] tabular-nums flex items-center gap-0.5 mt-1">
                    <Eye className="w-3 h-3" /> {post.views || 0}
                  </span>
                </div>

                <div className="flex flex-wrap gap-2 pt-1 border-t border-[#E4E8EF]/60">
                  <button
                    type="button"
                    data-testid={`edit-req-${post.id}`}
                    onClick={() => navigate(`/edit-requirement/${post.id}`)}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#0B1620] bg-[#EFF2F7] hover:bg-[#E4E8EF] border border-[#E4E8EF] transition-colors"
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  {post.is_open ? (
                    <button
                      type="button"
                      data-testid={`close-req-${post.id}`}
                      onClick={() => setPending({ action: 'close', post })}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#0B4F3F] bg-[#0B4F3F]/8 hover:bg-[#0B4F3F]/12 border border-[#0B4F3F]/20 transition-colors"
                    >
                      <PackageX className="w-3 h-3" /> Close
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`reopen-req-${post.id}`}
                      onClick={() => setPending({ action: 'reopen', post })}
                      className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#15803D] bg-[#15803D]/8 hover:bg-[#15803D]/12 border border-[#15803D]/20 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" /> Reopen
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`delete-req-${post.id}`}
                    onClick={() => setPending({ action: 'delete', post })}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-semibold text-[#B91C1C] bg-[#F6F8FB] hover:bg-[#B91C1C]/8 border border-[#B91C1C]/20 transition-colors ml-auto"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmSheet
        open={!!pending}
        onOpenChange={(o) => { if (!o) setPending(null); }}
        title={
          pending?.action === 'delete'
            ? 'Delete this requirement?'
            : pending?.action === 'close'
              ? 'Close this requirement?'
              : 'Reopen this requirement?'
        }
        description={
          pending?.action === 'delete'
            ? `"${pending?.post?.title}" will be permanently removed.`
            : pending?.action === 'close'
              ? `Farmers won't be able to respond to "${pending?.post?.title}" anymore. You can reopen it later.`
              : `"${pending?.post?.title}" will appear in the requirements feed again.`
        }
        confirmLabel={pending?.action === 'delete' ? 'Delete' : pending?.action === 'close' ? 'Close' : 'Reopen'}
        cancelLabel="Cancel"
        destructive={pending?.action === 'delete' || pending?.action === 'close'}
        icon={
          pending?.action === 'delete'
            ? <Trash2 className="w-6 h-6" strokeWidth={2.2} />
            : pending?.action === 'close'
              ? <PackageX className="w-6 h-6" strokeWidth={2.2} />
              : <RotateCcw className="w-6 h-6" strokeWidth={2.2} />
        }
        onConfirm={confirmAction}
        loading={busy}
        testId="my-req-confirm"
      />
    </AppShell>
  );
}
